import React from "react";
import { connect } from "react-redux";
import { Col, Form, Row } from "react-bootstrap";
import PropTypes from "prop-types";
import { NumericFormat as NumberFormat } from "react-number-format";
import Select from "react-select";

import Table from "../../../layout/table";
import Modal from "../../../layout/modal";
import { initialState } from "../../../layout/table/helpers";

import { isGuid } from "../../../utils/string";
import { format as formatDate } from "../../../utils/date";
import { currency as formatCurrency } from "../../../utils/number";
import { downloadCheckPdf } from "../../../utils/checks";
import { byAccount as getJournalLinesByAccount, save as saveJournalLine } from "../../../actions/journal.entry.lines";
import { reverse as reverseJournalEntry } from "../../../actions/journal.entries";
import { render as renderReport } from "../../../actions/report.template";
import { get as getSettings } from "../../../actions/settings";
import { loading } from "../../../actions/page";
import { EDIT_CHECK } from "../../../constants/permissions";

const safeParseJson = (value, fallback = {}) => {
  try {
    return typeof value === "string" ? JSON.parse(value) : (value || fallback);
  } catch (_err) {
    return fallback;
  }
};

class AccountEntries extends React.Component {
  sizeOptions = [
    { value: 10, label: "10" },
    { value: 25, label: "25" },
    { value: 50, label: "50" },
    { value: 100, label: "100" },
  ];

  selectStyles = {
    container: (base) => ({ ...base, minWidth: 86 }),
    control: (base) => ({
      ...base,
      minHeight: 26,
      height: 26,
      borderColor: "transparent",
      boxShadow: "none",
      backgroundColor: "transparent",
    }),
    valueContainer: (base) => ({ ...base, padding: "0 4px", height: 26 }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    indicatorsContainer: (base) => ({ ...base, height: 26 }),
    dropdownIndicator: (base) => ({ ...base, padding: 2 }),
    indicatorSeparator: () => ({ display: "none" }),
    singleValue: (base) => ({ ...base, fontSize: 12, color: "#6c757d" }),
    menu: (base) => ({ ...base, zIndex: 20 }),
  };

  constructor(props) {
    super(props);
    this.state = {
      ...initialState({ order: "createdAt", direction: "DESC", size: 10 }),
      count: 0,
      modal: {
        line: { show: false },
        error: { show: false },
      },
      selected: null,
      accountsSetting: {},
    };
  }

  componentDidMount = () => {
    this.loadData();
  };

  componentDidUpdate = (oldProps) => {
    if (oldProps.accountUuid !== this.props.accountUuid) this.loadData();
    if (oldProps.updated !== this.props.updated) this.loadData();
  };

  loadData = async () => {
    if (!isGuid(this.props.accountUuid)) return;

    const { page, size, order, direction } = this.state;
    const [result, accountsSetting] = await Promise.all([
      this.props.getJournalLinesByAccount({
        accountUuid: this.props.accountUuid,
        page,
        size,
        order,
        direction,
      }),
      this.props.getSettings({ authentic: true, name: "ACCOUNTS" }),
    ]);

    const count = Number(result?.count ?? 0);
    const pages = size > 0 ? Math.ceil(count / Number(size)) : 0;

    this.setState({
      data: result?.items || [],
      count,
      pages,
      accountsSetting: safeParseJson(accountsSetting?.ACCOUNTS || accountsSetting?.value || accountsSetting, {}),
    });
  };

  get sizeValue() {
    return this.sizeOptions.find((option) => option.value === Number(this.state.size)) || this.sizeOptions[0];
  }

  get headers() {
    return [
      {
        header: "Date",
        sortable: false,
        source: "JournalEntry.entryDate",
        className: "text-muted text-small text-uppercase w-15",
        onRender: (_value, row) => this.renderLink(formatDate(row?.JournalEntry?.entryDate), row),
      },
      {
        header: "Reference",
        sortable: false,
        source: "JournalEntry.referenceNumber",
        className: "text-muted text-small text-uppercase w-15",
        onRender: (_value, row) => this.renderLink(row?.JournalEntry?.referenceNumber || "", row),
      },
      {
        header: "Memo",
        sortable: false,
        source: "memo",
        className: "text-muted text-small text-uppercase w-45",
        onRender: (value, row) => this.renderMemo(value || row?.JournalEntry?.memo || "", row),
      },
      {
        header: "Debit",
        sortable: true,
        source: "debitAmount",
        className: "text-muted text-small text-uppercase w-10 text-end",
        dataClassName: "text-end",
        onRender: (value, row) => this.renderEntryValue(this.onCurrencyFormat(value), row),
      },
      {
        header: "Credit",
        sortable: true,
        source: "creditAmount",
        className: "text-muted text-small text-uppercase w-10 text-end",
        dataClassName: "text-end",
        onRender: (value, row) => this.renderEntryValue(this.onCurrencyFormat(value), row),
      },
    ];
  }

  isVoidedEntry = (row) => row?.JournalEntry?.status === "VOIDED";

  renderEntryValue = (text, row) => (
    <span className={this.isVoidedEntry(row) ? "text-decoration-line-through text-muted" : undefined}>
      {text}
    </span>
  );

  renderLink = (text, row) => (
    <button
      type="button"
      className={`btn btn-link p-0 align-baseline ${this.isVoidedEntry(row) ? "text-decoration-line-through text-muted" : ""}`}
      onClick={(e) => this.onLineClick(e, row)}
    >
      {text || "(entry)"}
    </button>
  );

  renderStatusBadge = (row) => {
    const status = row?.JournalEntry?.status;
    if (!status) return null;

    const label = status.charAt(0) + status.slice(1).toLowerCase();
    const badge = status === "VOIDED"
      ? "bg-danger"
      : status === "POSTED"
        ? "bg-primary"
        : "bg-light text-dark";

    return <span className={`badge ${badge}`}>{label}</span>;
  };

  renderClearedBadge = (row) => {
    if (!row?.clearedAt) return "";

    const content = `Cleared ${formatDate(row.clearedAt)}`;
    const className = "badge bg-success";
    if (!row?.bankReconciliationUuid) return <span className={className}>{content}</span>;

    return (
      <a
        href={`/dashboard/accounting/reconciliations/${row.bankReconciliationUuid}`}
        className={className}
      >
        {content}
      </a>
    );
  };

  renderMemo = (text, row) => (
    <span className={this.isVoidedEntry(row) ? "text-decoration-line-through text-muted" : undefined}>
      {this.renderLink(text, row)}
      <span className="ms-2 d-inline-flex gap-1 align-middle">
        {this.renderStatusBadge(row)}
        {this.renderClearedBadge(row)}
      </span>
    </span>
  );

  renderCleared = (row) => {
    if (!row?.clearedAt) return "";

    const content = `✓ ${formatDate(row.clearedAt)}`;
    if (!row?.bankReconciliationUuid) return this.renderEntryValue(content, row);

    return (
      <a
        href={`/dashboard/accounting/reconciliations/${row.bankReconciliationUuid}`}
        className={this.isVoidedEntry(row) ? "text-decoration-line-through text-muted" : undefined}
      >
        {content}
      </a>
    );
  };

  onCurrencyFormat = (value) => formatCurrency(Number(value || 0));

  getEntryMeta = (entry) => {
    if (!entry?.meta) return {};
    if (typeof entry.meta === "object") return entry.meta;

    try {
      return JSON.parse(entry.meta);
    } catch (_e) {
      return {};
    }
  };

  getCheckFromLine = (line) => {
    const entry = line?.JournalEntry || {};
    const meta = this.getEntryMeta(entry);
    const amount = Math.max(Number(line?.debitAmount || 0), Number(line?.creditAmount || 0));

    return {
      amount,
      checkDate: meta.checkDate || entry.entryDate,
      checkNumber: meta.checkNumber || entry.referenceNumber || "",
      payeeName: meta.payeeName || "",
      payeeAddress: meta.payeeAddress || "",
      memo: entry.memo || line?.memo || "",
      referenceNumber: entry.referenceNumber || meta.checkNumber || "",
    };
  };

  canGenerateCheck = (line) => {
    if (!this.canEditChecks) return false;

    const entry = line?.JournalEntry || {};
    const meta = this.getEntryMeta(entry);
    const check = this.getCheckFromLine(line);
    const isBusinessExpense = entry.sourceType === "BUSINESS_EXPENSE"
      || meta.category === "BUSINESS_EXPENSE"
      || Boolean(meta.businessExpenseUuid);

    return isBusinessExpense
      && Number(check.amount || 0) > 0
      && Boolean(String(check.payeeName || "").trim());
  };

  onLineClick = (e, row) => {
    e.preventDefault();
    this.setState({
      selected: {
        ...row,
        debitAmount: Number(row?.debitAmount || 0),
        creditAmount: Number(row?.creditAmount || 0),
        memo: row?.memo || "",
      },
      modal: {
        ...this.state.modal,
        line: { show: true },
      },
    });
  };

  onLineChange = (e) => {
    const { name, value } = e.target;
    this.setState((prev) => ({
      selected: {
        ...prev.selected,
        [name]: value,
      },
    }));
  };

  onLineHideModal = () => {
    this.setState((prev) => ({
      selected: null,
      modal: {
        ...prev.modal,
        line: { show: false },
      },
    }));
  };

  showError = (message) => {
    this.setState((prev) => ({
      modal: {
        ...prev.modal,
        error: {
          title: "Journal entry error",
          body: message,
          show: true,
        },
      },
    }));
  };

  onErrorHideModal = () => {
    this.setState((prev) => ({
      modal: {
        ...prev.modal,
        error: { show: false },
      },
    }));
  };

  onLineSave = async () => {
    const line = this.state.selected || {};

    this.props.loading(true);
    const result = await this.props.saveJournalLine({
      ...line,
      debitAmount: Number(line.debitAmount || 0),
      creditAmount: Number(line.creditAmount || 0),
    });
    this.props.loading(false);

    if (result?.success === false || !result) {
      this.showError(result?.error?.message || "The journal entry line could not be saved.");
      return;
    }

    this.onLineHideModal();
    await this.loadData();
  };

  onDownloadCheck = async () => {
    if (!this.canEditChecks) return;

    const line = this.state.selected || {};
    const check = this.getCheckFromLine(line);

    if (!this.canGenerateCheck(line)) {
      this.showError("This journal entry does not have enough business expense check information.");
      return;
    }

    this.props.loading(true);
    await downloadCheckPdf({
      render: this.props.renderReport,
      templateUuid: this.state.accountsSetting?.general?.check,
      fileName: `${formatDate(check.checkDate)} Check ${check.checkNumber || check.payeeName || "Business Expense"}.pdf`,
      check,
    });
    this.props.loading(false);
  };

  onReverseEntry = async () => {
    const entry = this.state.selected?.JournalEntry || {};

    if (!isGuid(entry.uuid)) {
      this.showError("Invalid journal entry.");
      return;
    }

    this.props.loading(true);
    const result = await this.props.reverseJournalEntry(entry.uuid);
    this.props.loading(false);

    if (result?.success === false || !result) {
      this.showError(result?.error?.message || "The journal entry could not be reversed.");
      return;
    }

    this.onLineHideModal();
    await this.loadData();
  };

  get canEditChecks() {
    return (this.props.auth?.permissions || []).includes(EDIT_CHECK);
  }

  onPageChange = async (page) => {
    this.setState({ page: Number(page) }, this.loadData);
  };

  onColumnClick = async (header) => {
    if (!header?.sortable) return;

    const order = header.source;
    const direction = this.state.order === order && this.state.direction === "ASC" ? "DESC" : "ASC";

    this.setState({ order, direction, page: 0 }, this.loadData);
  };

  onSize = async (size) => {
    this.setState({ size: Number(size), page: 0 }, this.loadData);
  };

  renderLineModal = () => {
    const line = this.state.selected || {};
    const entry = line?.JournalEntry || {};
    const posted = entry.status === "POSTED";
    const canGenerateCheck = this.canGenerateCheck(line);

    return (
      <Modal
        show={this.state.modal.line.show}
        title="Journal Entry Line"
        size="lg"
        onCancel={this.onLineHideModal}
        buttons={[
          {
            caption: "Reverse",
            onClick: this.onReverseEntry,
            variant: "outline-danger",
            display: posted,
            enabled: true,
          },
          {
            caption: "Print Check",
            onClick: this.onDownloadCheck,
            variant: "outline-primary",
            display: canGenerateCheck,
            enabled: true,
          },
          {
            caption: "ok",
            onClick: posted ? this.onLineHideModal : this.onLineSave,
            variant: "primary",
            display: true,
            enabled: true,
          },
          {
            default: true,
            caption: "cancel",
            onClick: this.onLineHideModal,
            variant: "secondary",
            display: true,
            enabled: true,
          },
        ]}
      >
        {posted ? (
          <div className="alert alert-info mb-3">
            Posted journal entries are read-only. Corrections should be posted as reversals.
          </div>
        ) : null}

        <Row>
          <Col xs="12" md="4">
            <div className="mb-3 top-label">
              <Form.Control type="text" value={formatDate(entry.entryDate)} disabled />
              <Form.Label>DATE</Form.Label>
            </div>
          </Col>

          <Col xs="12" md="4">
            <div className="mb-3 top-label">
              <Form.Control type="text" value={entry.status || ""} disabled />
              <Form.Label>STATUS</Form.Label>
            </div>
          </Col>

          <Col xs="12" md="4">
            <div className="mb-3 top-label">
              <Form.Control type="text" value={entry.referenceNumber || ""} disabled />
              <Form.Label>REFERENCE</Form.Label>
            </div>
          </Col>

          <Col xs="12" md="6">
            <div className="mb-3 top-label">
              <NumberFormat
                name="debitAmount"
                value={line.debitAmount ?? 0}
                thousandSeparator
                allowNegative={false}
                inputMode="decimal"
                decimalScale={2}
                prefix="$"
                className="text-end form-control mb-2"
                disabled
                onValueChange={({ floatValue }) => {
                  this.onLineChange({ target: { name: "debitAmount", value: floatValue ?? 0 } });
                }}
              />
              <Form.Label>DEBIT</Form.Label>
            </div>
          </Col>

          <Col xs="12" md="6">
            <div className="mb-3 top-label">
              <NumberFormat
                name="creditAmount"
                value={line.creditAmount ?? 0}
                thousandSeparator
                allowNegative={false}
                inputMode="decimal"
                decimalScale={2}
                prefix="$"
                className="text-end form-control mb-2"
                disabled
                onValueChange={({ floatValue }) => {
                  this.onLineChange({ target: { name: "creditAmount", value: floatValue ?? 0 } });
                }}
              />
              <Form.Label>CREDIT</Form.Label>
            </div>
          </Col>

          <Col xs="12">
            <div className="mb-3 top-label">
              <Form.Control
                as="textarea"
                rows={3}
                name="memo"
                value={line.memo || ""}
                disabled={posted}
                onChange={this.onLineChange}
              />
              <Form.Label>MEMO</Form.Label>
            </div>
          </Col>
        </Row>
      </Modal>
    );
  };

  render() {
    if (!isGuid(this.props.accountUuid)) return null;

    return (
      <>
        <Col xs="12" className="d-flex align-items-center justify-content-between mt-5">
          <h2 className="small-title mb-0">Entries ({this.state.count})</h2>

          <div className="d-flex align-items-center gap-2">
            <span className="text-muted small">Rows</span>
            <Select
              classNamePrefix="ra"
              value={this.sizeValue}
              options={this.sizeOptions}
              onChange={(option) => this.onSize(option?.value)}
              isSearchable={false}
              styles={this.selectStyles}
              aria-label="Rows per page"
            />
          </div>
        </Col>
        <Col xs="12">
          <Table
            headers={this.headers}
            page={this.state.data}
            size={this.state.size}
            index={this.state.page}
            pages={this.state.pages}
            onColumnClick={this.onColumnClick}
            onPageChange={this.onPageChange}
            searchable={false}
            sizable={false}
          />
        </Col>

        {this.renderLineModal()}

        <Modal
          show={this.state.modal.error.show}
          title={this.state.modal.error.title}
          body={this.state.modal.error.body}
          buttons={[
            {
              default: true,
              caption: "ok",
              onClick: this.onErrorHideModal,
              variant: "secondary",
              display: true,
              enabled: true,
            },
          ]}
        />
      </>
    );
  }
}

AccountEntries.propTypes = {
  auth: PropTypes.object.isRequired,
  accountUuid: PropTypes.string,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default connect(mapStateToProps, {
  loading,
  getJournalLinesByAccount,
  saveJournalLine,
  reverseJournalEntry,
  renderReport,
  getSettings,
})(AccountEntries);
